// src/pages/AdminPanel/index.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useProfile } from "context/ProfileContext";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import TabMenu from "components/TabMenu";

import DataUploader from "./DataUploader";
import OrgConfig from "./OrgSetup";
import AuditLogs from "./AuditLogs";
import Users from "./Users";
import UserAccess from "./UserAccess";
import LibraryAccess from "./LibraryAccess";
import SecurityMaster from "./SecurityMaster";
import ExternalIdentifiers from "./ExternalIdentifiers";

/**
 * AdminPanel
 * ------------------------------------------------------------------
 * UI-only alignment layer for admin functionality.
 * Backend access, JWT claims, and RLS are assumed correct and stable.
 *
 * Roles:
 *  • Platform Admin   -> is_admin
 *  • Org Admin        -> is_org_admin
 *  • Regular User     -> no access
 */

export default function AdminPanel() {
  const { profile, loading: profileLoading } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(null);

  /* ------------------------------------------------------------------
   * Role resolution
   * ------------------------------------------------------------------ */
  const isPlatformAdmin = !!profile?.is_admin;
  const isOrgAdmin = !!profile?.is_org_admin;
  const hasAdminAccess = isPlatformAdmin || isOrgAdmin;

  /* ------------------------------------------------------------------
   * Tab definitions (derived from role)
   * ------------------------------------------------------------------ */
  const tabs = useMemo(() => {
    if (isPlatformAdmin) {
      return [
        { key: "users", label: "Users" },
        { key: "orgs", label: "Orgs" },
        { key: "org-user-permissions", label: "Org-User Permissions" },
        { key: "library", label: "DS Library" },
        { key: "external-identifiers", label: "External IDs" },
        { key: "security-master", label: "FP Security Master" },
        { key: "import", label: "Upload Data" },
        { key: "logs", label: "Audit Logs" },
      ];
    }

    if (isOrgAdmin) {
      return [{ key: "org-user-permissions", label: "Org-User Permissions" }];
    }

    return [];
  }, [isPlatformAdmin, isOrgAdmin]);

  const tabKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);

  /* ------------------------------------------------------------------
   * Route guard
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!profileLoading && !hasAdminAccess) {
      navigate("/dashboard", { replace: true });
    }
  }, [profileLoading, hasAdminAccess, navigate]);

  /* ------------------------------------------------------------------
   * Sync active tab from URL
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!tabs.length) return;

    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get("tab");

    if (tabFromUrl && tabKeys.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      setActiveTab(tabs[0].key);
      navigate(`?tab=${tabs[0].key}`, { replace: true });
    }
  }, [location.search, tabs, tabKeys, navigate]);

  /* ------------------------------------------------------------------
   * Tab change handler
   * ------------------------------------------------------------------ */
  const setTabAndUrl = (tab) => {
    if (!tabKeys.includes(tab)) return;
    setActiveTab(tab);
    navigate(`?tab=${tab}`, { replace: false });
  };

  /* ------------------------------------------------------------------
   * Loading / guard states
   * ------------------------------------------------------------------ */
  if (profileLoading) {
    return <div className="p-6 text-app">Loading admin panel...</div>;
  }

  if (!hasAdminAccess) {
    return null;
  }

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */
  return (
    <Page title="Admin Panel">
      <div className="space-y-6">
        <SectionCard noPad>
          <div className="px-5 pt-5">
            <TabMenu
              tabs={tabs}
              activeTab={activeTab}
              onChange={setTabAndUrl}
              ariaLabel="Admin Panel Tabs"
            />
          </div>

          {activeTab === "users" && (
            <div
              id="tabpanel-users"
              role="tabpanel"
              aria-labelledby="tab-users"
              className="space-y-6 p-5"
            >
              <Users />
            </div>
          )}

          {activeTab === "org-user-permissions" && (
            <div
              id="tabpanel-org-user-permissions"
              role="tabpanel"
              aria-labelledby="tab-org-user-permissions"
              className="space-y-6 p-5"
            >
              <UserAccess />
            </div>
          )}

          {isPlatformAdmin && activeTab === "orgs" && (
            <div
              id="tabpanel-orgs"
              role="tabpanel"
              aria-labelledby="tab-orgs"
              className="p-5"
            >
              <OrgConfig />
            </div>
          )}

          {isPlatformAdmin && activeTab === "library" && (
            <div
              id="tabpanel-library"
              role="tabpanel"
              aria-labelledby="tab-library"
              className="space-y-6 p-5"
            >
              <LibraryAccess />
            </div>
          )}

          {isPlatformAdmin && activeTab === "security-master" && (
            <div
              id="tabpanel-security-master"
              role="tabpanel"
              aria-labelledby="tab-security-master"
              className="space-y-6 p-5"
            >
              <SecurityMaster />
            </div>
          )}

          {isPlatformAdmin && activeTab === "external-identifiers" && (
            <div
              id="tabpanel-external-identifiers"
              role="tabpanel"
              aria-labelledby="tab-external-identifiers"
              className="space-y-6 p-5"
            >
              <ExternalIdentifiers />
            </div>
          )}

          {isPlatformAdmin && activeTab === "import" && (
            <div
              id="tabpanel-import"
              role="tabpanel"
              aria-labelledby="tab-import"
              className="p-5"
            >
              <DataUploader />
            </div>
          )}

          {isPlatformAdmin && activeTab === "logs" && (
            <div
              id="tabpanel-logs"
              role="tabpanel"
              aria-labelledby="tab-logs"
              className="p-5"
            >
              <AuditLogs />
            </div>
          )}
        </SectionCard>
      </div>
    </Page>
  );
}
